/**
 * #/matches — el archivo de la liga.
 *
 * Es la portada del portal: qué partidos hay, cuán cargados están y desde acá
 * se bajan los documentos del partido sin entrar al detalle.
 *
 * Son TRES y no hay que confundirlos (el botón «Planilla» abría el reporte):
 *  - **Planilla** (`scoresheet.html`): el acta de mesa, para imprimir y firmar.
 *    Se arma sólo con el log, así que nunca depende de `@scout/stats`.
 *  - **Reporte** (`report.html`): la estadística del partido (fundamentos,
 *    sideout/break, jugadores).
 *  - **`.dvw`**: el export para abrir el partido en DataVolley.
 */
import { useMemo, useState } from 'react';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading } from '../components/States';
import { openHtmlInTab, saveTextAs } from '../browser';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient, MatchDocument } from '../lib/api-client';
import { dateTime } from '../lib/format';
import { filterMatches } from '../lib/rankings';
import { matchHash, mesaHash } from '../lib/router';

export function MatchesPage({ client }: { client: ApiClient }): JSX.Element {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const matches = useAsync(() => client.listMatches(), [client]);
  const t = useT();

  const visible = useMemo(
    () => filterMatches(matches.data ?? [], query),
    [matches.data, query],
  );

  const download = async (matchId: string): Promise<void> => {
    setBusy(`${matchId}:dvw`);
    setNotice(null);
    try {
      const file = await client.downloadDvw(matchId);
      saveTextAs(`${matchId}.dvw`, file.body, file.contentType);
    } catch (err) {
      setNotice(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(null);
    }
  };

  /** Abre en una pestaña el acta (`scoresheet`) o la estadística (`report`). */
  const openDocument = async (matchId: string, kind: MatchDocument): Promise<void> => {
    setBusy(`${matchId}:${kind}`);
    setNotice(null);
    try {
      const file = await client.downloadMatchDocument(matchId, kind);
      openHtmlInTab(file.body);
    } catch (err) {
      setNotice(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t('matches.title')}</h1>
        <div className="row">
          <input
            className="search"
            type="search"
            placeholder={t('matches.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('matches.searchLabel')}
          />
          <button type="button" onClick={matches.reload}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {notice ? <ErrorBox message={notice} /> : null}
      {matches.status === 'error' ? (
        <ErrorBox message={errorMessage(matches.error)} onRetry={matches.reload} />
      ) : null}

      <Card>
        {matches.status === 'loading' && matches.first ? <Loading what={t('matches.loadingWhat')} /> : null}

        {matches.status !== 'loading' && (matches.data ?? []).length === 0 && matches.status !== 'error' ? (
          <Empty>{t('matches.empty')}</Empty>
        ) : null}

        {visible.length === 0 && (matches.data ?? []).length > 0 ? (
          <Empty>{t('matches.noMatchesFor', { query })}</Empty>
        ) : null}

        {visible.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('matches.colMatch')}</th>
                  <th>{t('matches.colRules')}</th>
                  <th className="num">{t('matches.colEvents')}</th>
                  <th className="num">{t('matches.colLastSeq')}</th>
                  <th>{t('matches.colUpdated')}</th>
                  <th>{t('matches.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((match) => (
                  <tr key={match.matchId}>
                    <td>
                      <a className="match-link" href={matchHash(match.matchId)}>
                        {match.name || match.matchId}
                      </a>
                      <div className="hint">{match.matchId}</div>
                    </td>
                    <td>{match.sportConfigId}</td>
                    <td className="num">{match.eventCount}</td>
                    <td className="num">{match.lastSeq}</td>
                    <td>{dateTime(match.updatedAt)}</td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {/* Mesa de control del acta (#/mesa): el anotador la
                            encuentra desde acá, sin tipear el hash. */}
                        <a href={mesaHash(match.matchId)} style={{ textDecoration: 'none' }}>
                          <button type="button" className="small" title={t('matches.mesaTitle')}>
                            {t('matches.mesa')}
                          </button>
                        </a>
                        <button
                          type="button"
                          className="small"
                          disabled={busy !== null}
                          title={t('matches.scoresheetTitle')}
                          onClick={() => void openDocument(match.matchId, 'scoresheet')}
                        >
                          {busy === `${match.matchId}:scoresheet` ? '…' : t('matches.scoresheet')}
                        </button>
                        <button
                          type="button"
                          className="small"
                          disabled={busy !== null}
                          title={t('matches.reportTitle')}
                          onClick={() => void openDocument(match.matchId, 'report')}
                        >
                          {busy === `${match.matchId}:report` ? '…' : t('matches.report')}
                        </button>
                        <button
                          type="button"
                          className="small"
                          disabled={busy !== null}
                          onClick={() => void download(match.matchId)}
                        >
                          {busy === `${match.matchId}:dvw` ? '…' : t('matches.dvw')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
